import { Grid, Paper, Box, Divider, Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import invoiceMissionService, { createAutogiroFile, sendAutogiroFile } from 'src/shared/services/invoiceMissionService';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { toast } from 'react-toastify';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { FSVersionEnum } from 'src/shared/models/enums/FSVersionEnum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useGlobals } from 'src/shared/contexts/appContext';
import InvoiceDataGrid from '../components/invoiceDataGrid';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import { SendingSection } from '../components/sendingSection';
import { SendingSessionSection } from '../components/sendingSessionSection';
import InvoiceTabs, { invoiceTabs } from '../components/invoiceTabs';
import { InvoiceMissionDetailTabs } from '../components/invoiceMissionDetailTabs';

// Aviseringar (uppdrag) detaljvy
export const InvoiceMissionDetailsPage = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string; sendingType: string }>();
  const navigate = useNavigate();
  const { currentUser } = useGlobals();
  const [isFSVersion2, setIsFSVersion2] = useState<boolean>(true);
  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const [loading, setIsLoading] = useState(false);
  const [refetchDataGrid, setRefetchDataGrid] = useState(false);
  const [invoiceMission, setInvoiceMission] = useState<InvoiceMissionResponse>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [autogiroLoading, setAutogiroLoading] = useState(false);

  const isAutogiro = invoiceMission?.type === InvoiceMissionTypeTextEnum.autogiro;

  const [invoiceMissionDetailFilter, setInvoiceMissionDetailFilter] = useLocalStorage(LocalStorageKeys.InvoiceMissionDetailFilter, {
    status: invoiceTabs.filter((x) => x.key === 'All')[0],
  } as unknown as InvoiceSearchFilter);
  const isMounted = useIsMounted();
  const lastFetchedSendingType = useRef<string | undefined>(params.sendingType);

  const searchFilter: InvoiceSearchFilter = useMemo(() => {
    return {
      ownerId: undefined,
      invoiceMissionId: invoiceMission?.invoiceMission_id,
      searchString: '',
      status: invoiceMissionDetailFilter?.status,
      isSent: invoiceMissionDetailFilter?.isSent,
      sendingType: params.sendingType ? Number(params.sendingType) : InvoiceSendingTypeIntegerEnum.NotSet,
      pageIndex: invoiceMissionDetailFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      pageSize: invoiceMissionDetailFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
    } as unknown as InvoiceSearchFilter;
  }, [
    invoiceMission?.invoiceMission_id,
    invoiceMissionDetailFilter?.isSent,
    invoiceMissionDetailFilter?.pageIndex,
    invoiceMissionDetailFilter?.pageSize,
    invoiceMissionDetailFilter?.status,
    params.sendingType,
  ]);

  useEffect(() => {
    if (searchFilter.invoiceMissionId?.toString() !== params.id) {
      setInvoiceMissionDetailFilter((prev) => ({
        ...prev,
        sendingType: 0,
      }));
    }
  }, [params.id, searchFilter.invoiceMissionId, setInvoiceMissionDetailFilter]);

  const fetchInvoiceMission = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isMounted() && params.id) {
        const result = await invoiceMissionService.getById(params.id, searchFilter.sendingType, isSuperAdmin ? true : undefined);
        setInvoiceMission(result.data);
        lastFetchedSendingType.current = params.sendingType;
        if (result.data.source !== FSVersionEnum.FS20) {
          setIsFSVersion2(false);
        }
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [isMounted, isSuperAdmin, params.id, params.sendingType, searchFilter.sendingType]);

  useEffect(() => {
    fetchInvoiceMission();
  }, [fetchInvoiceMission]);

  const handleCreateAutogiroFile = async () => {
    if (!invoiceMission) return;
    setAutogiroLoading(true);
    toast.info(t('invoice:invoiceMissionPage.autogiroFileProcessing') as string);
    try {
      await createAutogiroFile(invoiceMission.invoiceMission_id);
      toast.success(t('invoice:invoiceMissionPage.createAutogiroFileSuccess') as string);
      fetchInvoiceMission();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:invoiceMissionPage.createAutogiroFileError') as string);
    } finally {
      setAutogiroLoading(false);
    }
  };

  const handleSendAutogiroFile = async () => {
    if (!invoiceMission) return;
    setAutogiroLoading(true);
    toast.info(t('invoice:invoiceMissionPage.autogiroFileProcessing') as string);
    try {
      await sendAutogiroFile(invoiceMission.invoiceMission_id);
      toast.success(t('invoice:invoiceMissionPage.sendAutogiroFileSuccess') as string);
      fetchInvoiceMission();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('invoice:invoiceMissionPage.sendAutogiroFileError') as string);
    } finally {
      setAutogiroLoading(false);
    }
  };

  const loadingCallback = useCallback(
    (isLoading: boolean) => {
      if (isMounted()) {
        ConsoleHelper.log('loadingCallback InvoiceMissionDetailsPage datagrid', isLoading);
        setIsLoading(false);
      }
    },
    [isMounted],
  );

  const onHandleDelete = async (id: number) => {
    try {
      await invoiceMissionService.deleteInvoiceMission(id);
      if (isMounted()) {
        toast.success(<>{t('invoice:invoiceMissionPage.deleteInvoiceMissionSuccess')}</>);
        navigate(`${RoutesEnum.InvoiceMission}`);
      }
    } catch (e) {
      toast.error(<>{t('invoice:invoiceMissionPage.deleteInvoiceMissionError')}</>);
    } finally {
      if (isMounted()) {
        setOpenDeleteDialog(false);
      }
    }
  };

  const handleRefetch = useCallback(
    (refetch: boolean) => {
      if (refetch) {
        fetchInvoiceMission();
        setRefetchDataGrid(true);
      }
    },
    [fetchInvoiceMission],
  );

  return (
    <Grid container>
      <LoadingOverlay loading={loading} />
      <Grid container justifyContent='space-between' mb={2.5}>
        <Grid item xs='auto' sx={{ display: 'flex', alignItems: 'center' }}>
          <MainContentHeader title={`${invoiceMission?.missionName}`} isLoading={loading} />
        </Grid>
        {invoiceMission && (
          <Grid item xs='auto' sx={{ marginTop: { xs: 1, sm: 0 }, display: 'flex', gap: 1 }}>
            {isSuperAdmin && isAutogiro && (
              <>
                <Button size='medium' variant='outlined' disabled={autogiroLoading} onClick={handleCreateAutogiroFile}>
                  {t('invoice:invoiceMissionPage.createAutogiroFile')}
                </Button>
                <Button size='medium' variant='outlined' disabled={autogiroLoading} onClick={handleSendAutogiroFile}>
                  {t('invoice:invoiceMissionPage.sendAutogiroFile')}
                </Button>
              </>
            )}
            {isFSVersion2 && (
              <Button size='medium' onClick={() => setOpenDeleteDialog(true)} disableElevation variant='outlined' color='error' type='button'>
                {t('invoice:invoiceMissionPage.deleteInvoiceMission')}
              </Button>
            )}
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ px: 3, overflow: 'hidden' }}>
          {invoiceMission && (
            <GridWrapper>
              <Grid item xs={12} ml={-3}>
                <InvoiceMissionDetailTabs dataList={invoiceMission.dataList} />
              </Grid>

              <Grid item xs={12} lg={invoiceMission.type === InvoiceMissionTypeTextEnum.member ? 6 : 12}>
                <ContentSection header={t('invoice:invoiceMissionPage.mission')}>
                  <SendingSection
                    key={params.sendingType}
                    invoiceMission={invoiceMission}
                    loading={loading || lastFetchedSendingType.current !== params.sendingType}
                  />
                </ContentSection>
              </Grid>

              {invoiceMission.type === InvoiceMissionTypeTextEnum.member && (
                <Grid item xs={12} lg={6}>
                  <ContentSection header={t('invoice:invoiceMissionPage.sending')}>
                    <SendingSessionSection
                      key={params.sendingType}
                      invoiceMission={invoiceMission}
                      loading={loading || lastFetchedSendingType.current !== params.sendingType}
                      isFSVersion2={isFSVersion2}
                      refetchMission={handleRefetch}
                    />
                  </ContentSection>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ mx: -3 }}>
                  <InvoiceTabs
                    initialValue={searchFilter.status || undefined}
                    disableAll={loading}
                    callback={(val) => {
                      setInvoiceMissionDetailFilter((prev) => ({ ...prev, ...{ status: val, isSent: val.isSent, pageIndex: 0 } }));
                    }}
                    includeSentStatusTabs
                  />
                </Box>
                <Divider sx={{ mx: -3 }} />
                <Box className='section-inset' sx={{ mx: -3 }}>
                  <InvoiceDataGrid
                    loadingCallback={loadingCallback}
                    searchFilter={searchFilter}
                    searchFilterCallback={(newFilter) => {
                      setInvoiceMissionDetailFilter((prev) => ({
                        ...prev,
                        ...newFilter,
                      }));
                    }}
                    hideCheckBoxSelection
                    refetchData={refetchDataGrid}
                    setRefetchData={(val: boolean) => setRefetchDataGrid(val)}
                  />
                </Box>
              </Grid>
            </GridWrapper>
          )}
        </Paper>
      </Grid>
      {invoiceMission && (
        <DeleteConfirmationDialog
          title={t('common:confirmDelete')}
          value={invoiceMission.invoiceMission_id.toString()}
          modaltext={t('common:irreversibleAction')}
          onClose={(value) => {
            if (value) {
              const id = Number(value.id);
              onHandleDelete(id);
            } else {
              setOpenDeleteDialog(false);
            }
          }}
          open={openDeleteDialog}
        />
      )}
    </Grid>
  );
};
